import React, { useContext, useState, useEffect } from "react";
import "./style.css";
import RegistrationContext from "../../contexts/RegistrationContext";
import { FileText, FileSearch, Trash2, Gift } from "lucide-react";
import axios from "axios";
import { base_url, stripe_key } from "../../config";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(stripe_key);

function CheckoutForm({ amount, onBack, setMessage }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: base_url + "/api/extra_payment/" },
    });
    if (error) setMessage(error.message);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <PaymentElement options={{ layout: "tabs" }} />
      <div className="flex gap-3 justify-between items-center">
        <button type="button" onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 underline">
          ← Back
        </button>
        <button type="submit" disabled={!stripe} className="button">
          Pay {amount} €
        </button>
      </div>
    </form>
  );
}

function FreeConfirmation({ paper, onConfirm, onBack, loading }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="bg-green-50 border border-green-200 rounded-lg px-5 py-4 flex items-start gap-3">
        <Gift size={18} className="shrink-0 mt-0.5 text-green-600" />
        <div>
          <p className="text-sm font-semibold text-green-800 mb-1">First paper included for free</p>
          <p className="text-sm text-green-700">
            Your registration includes one complimentary paper registration.
          </p>
        </div>
      </div>

      <div className="border border-gray-100 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm text-gray-800">{paper.id} — {paper.title}</span>
          <span className="text-sm font-semibold text-green-600">Free</span>
        </div>
      </div>

      <div className="flex gap-3 justify-between items-center">
        <button type="button" onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 underline">
          ← Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className="button disabled:opacity-50"
        >
          {loading ? "Registering…" : "Confirm free registration"}
        </button>
      </div>
    </div>
  );
}

function Summary({ onDone }) {
  const { setPaperOpen, paperSelected, setPaperSelected, setError, prices, data, reload, setReload } =
    useContext(RegistrationContext);

  const [papers, setPapers] = useState([]);
  const [cart, setCart] = useState([]);
  const [clientSecret, setClientSecret] = useState("");
  const [amount, setAmount] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [showFreeConfirm, setShowFreeConfirm] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const hasFreeSlot = !data.papers_registered || data.papers_registered.length === 0;

  useEffect(() => {
    axios.get(`${base_url}/api/papers`).then((r) => setPapers(r.data)).catch(() => {});
    setPaperSelected("");
  }, []);

  useEffect(() => {
    if (!paperSelected || paperSelected === "") return;
    if (cart.some((p) => p.id === paperSelected)) return;
    if (cart.length >= 3) { setError("You cannot add more than 3 extra papers."); return; }
    const paper = papers.find((p) => paperSelected.includes(p.id));
    if (!paper) return;
    // First paper is free if no papers registered yet
    const isFree = hasFreeSlot && cart.length === 0;
    setCart((prev) => [...prev, { id: paperSelected, title: paper.title, price: isFree ? 0 : (prices?.paper ?? 0) }]);
  }, [paperSelected]);

  const handleFreeConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${base_url}/api/add_paper`, { paper_key: cart[0].id });
      setReload(reload + 1);
      onDone?.();
    } catch (error) {
      setMessage("Unexpected error. " + (error.response?.data?.message ?? ""));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    // If first paper is free, show confirmation instead of Stripe
    if (hasFreeSlot && cart.length > 0 && cart[0].price === 0) {
      if (cart.length === 1) {
        setShowFreeConfirm(true);
        return;
      }
      // Free paper + paid papers: register free one first, then checkout paid ones
      setLoading(true);
      setError("");
      try {
        await axios.post(`${base_url}/api/add_paper`, { paper_key: cart[0].id });
      } catch (error) {
        setMessage("Unexpected error. " + (error.response?.data?.message ?? ""));
        setLoading(false);
        return;
      }
      const paidCart = cart.slice(1);
      if (paidCart.length === 0) { setReload(reload + 1); onDone?.(); return; }
      try {
        const response = await axios.post(`${base_url}/api/extra`, {
          cart: paidCart.map((p) => ({ id: p.id, type: "paper", qty: 1, description: p.title, price: p.price })),
        });
        setClientSecret(response.data.clientSecret);
        const stripe = await stripePromise;
        const { paymentIntent } = await stripe.retrievePaymentIntent(response.data.clientSecret);
        setAmount(paymentIntent.amount / 100);
        setShowPayment(true);
      } catch (error) {
        setMessage("Unexpected error. " + (error.response?.data?.message ?? ""));
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${base_url}/api/extra`, {
        cart: cart.map((p) => ({ id: p.id, type: "paper", qty: 1, description: p.title, price: p.price })),
      });
      setClientSecret(response.data.clientSecret);
      const stripe = await stripePromise;
      const { paymentIntent } = await stripe.retrievePaymentIntent(response.data.clientSecret);
      setAmount(paymentIntent.amount / 100);
      setShowPayment(true);
    } catch (error) {
      setError("Unexpected error. " + (error.response?.data?.message ?? ""));
    }
    setLoading(false);
  };

  const total = cart.reduce((sum, p) => sum + p.price, 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100" style={{ background: "#f8faff" }}>
          <FileText size={18} color="#002E78" />
          <span className="font-semibold text-[#002E78]">Add Additional Paper</span>
        </div>

        <div className="px-6 py-5">
          {showFreeConfirm ? (
            <FreeConfirmation
              paper={cart[0]}
              onConfirm={handleFreeConfirm}
              onBack={() => setShowFreeConfirm(false)}
              loading={loading}
            />
          ) : !showPayment ? (
            <>
              <p className="text-sm text-gray-500 mb-4">
                {hasFreeSlot
                  ? <>You can include one paper in your registration. Additional papers cost <span className="font-semibold text-[#002E78]">{prices?.paper} €</span>.</>
                  : <>Each additional paper costs <span className="font-semibold text-[#002E78]">{prices?.paper} €</span>.</>
                }
              </p>

              {/* Paper list */}
              {cart.length > 0 && (
                <ul className="divide-y divide-gray-50 mb-4 border border-gray-100 rounded-lg overflow-hidden">
                  {cart.map((paper, index) => (
                    <li key={index} className="flex items-center gap-3 px-4 py-3">
                      {paper.price === 0 && (
                        <span className="shrink-0 text-xs font-bold text-white px-2 py-0.5 rounded bg-green-600">FREE</span>
                      )}
                      <span className="text-sm text-gray-800 flex-grow">{paper.id} — {paper.title}</span>
                      <span className="text-sm text-gray-500 shrink-0">{paper.price === 0 ? "Free" : `${paper.price} €`}</span>
                      <Trash2
                        size={14}
                        className="shrink-0 cursor-pointer text-gray-400 hover:text-red-500"
                        onClick={() => setCart(cart.filter((_, i) => i !== index))}
                      />
                    </li>
                  ))}
                  <li className="flex justify-between px-4 py-3 bg-gray-50 text-sm font-semibold">
                    <span>Total</span>
                    <span>{total === 0 ? "Free" : `${total} €`}</span>
                  </li>
                </ul>
              )}

              {/* Actions */}
              <div className="flex gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setPaperOpen(true)}
                  className="button flex items-center gap-2"
                  style={{ height: "36px", padding: "0 12px", fontSize: "13px" }}
                >
                  <FileSearch size={14} /> Add paper
                </button>
                {cart.length > 0 && (
                  <button
                    type="button"
                    onClick={handleCheckout}
                    disabled={loading}
                    className="button disabled:opacity-50"
                    style={{ height: "36px", padding: "0 16px", fontSize: "13px" }}
                  >
                    {loading ? "Processing…" : total === 0 ? "Confirm" : `Checkout — ${total} €`}
                  </button>
                )}
              </div>
              {message && <p className="mt-3 text-red-500 text-sm">{message}</p>}
            </>
          ) : (
            <Elements options={{ clientSecret, appearance: { theme: "stripe" } }} stripe={stripePromise}>
              <CheckoutForm amount={amount} onBack={() => setShowPayment(false)} setMessage={setMessage} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
}

export default Summary;
